import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, LinearProgress, Tooltip } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import contentDisposition from 'content-disposition';
import { isNumber } from 'lodash-es';

const styles = {
  root: {
    position: 'relative',
    display: 'inline-block',

    '& .MuiButton-root, & .MuiButton-label': {
      display: 'flex',
      alignItems: 'center',

      '& svg': {
        marginRight: '5px',
      },
    },
  },

  progress: {
    position: 'absolute',
    left: 1,
    bottom: 1,
    width: 'calc(100% - 4px)',
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
};

DownloadButton.propTypes = {
  preDownload: PropTypes.func,
  download: PropTypes.func,
  autoDownload: PropTypes.func,
  postDownload: PropTypes.func,
};

export function DownloadButton(props) {
  const {
    preDownload,
    download: downloadOnClick,
    autoDownload,
    postDownload,
    children,
    ...rest
  } = props;

  const [isDownloading, setDownloading] = useState(false);
  const [file, setFile] = useState();
  const [fileName, setFileName] = useState();
  const [fileHref, setFileHref] = useState();
  const [error, setError] = useState();
  const [progress, setProgress] = useState();
  const downloadLink = useRef();
  const progressType = useMemo(() => {
    if (isNumber(progress)) return 'buffer';
    return 'indeterminate';
  }, [progress]);

  useEffect(() => {
    function getFilename() {
      try {
        if (file.filename) return file.filename;

        const header = file.headers['content-disposition'];
        const { parameters } = contentDisposition.parse(header);
        return parameters.filename;
      } catch (e) {
        return 'download.zip';
      }
    }

    if (file) {
      setFileName(getFilename());
      setFileHref(window.URL.createObjectURL(new Blob([file.data])));
    }
  }, [file]);

  useEffect(() => {
    if (downloadLink.current) downloadLink.current.click();
  }, [downloadLink, fileHref, fileName]);

  useEffect(() => {
    if (isDownloading || !autoDownload) return;
    setDownloading(true);
    setError(null);
    autoDownload()
      .then(setFile)
      .catch(setError)
      .finally(() => {
        if (postDownload) postDownload();
        setDownloading(false);
      });
  }, [autoDownload, isDownloading, postDownload]);

  const button = (
    <Button {...rest} sx={styles.root} onClick={onClick}>
      {error ? <ErrorIcon color="error" /> : null}
      {children}
    </Button>
  );

  const buttonWithTooltip = error ? (
    <Tooltip title="We had some trouble with that download. Please try again.">
      {button}
    </Tooltip>
  ) : (
    button
  );

  return (
    <Box sx={styles.root}>
      {buttonWithTooltip}
      {isDownloading && (
        <LinearProgress
          variant={progressType}
          value={progress}
          valueBuffer={progress}
          sx={styles.progress}
        />
      )}
      {fileHref && fileName && (
        <a ref={downloadLink} href={fileHref} download={fileName} />
      )}
    </Box>
  );

  async function onClick() {
    if (preDownload) return preDownload();
    if (isDownloading) return;

    setDownloading(true);
    setError(null);

    try {
      setFile(await downloadOnClick(setProgress));
    } catch (e) {
      setError(e);
    }

    if (postDownload) postDownload();
    setDownloading(false);
  }
}
